import moment from 'moment-timezone'

export default class FormatUtilities {
    /**
     * This method formats phone numbers to properly display on the UI (ex: (123) 456 - 2345))
     */

    static formatAddress = address => {
        let firstAddressLine = ''
        let secondAddressLine = ''

        if (!address.street) {
            firstAddressLine = address.compliment || ''
        } else {
            firstAddressLine = address.street + `${!address.compliment ? '' : ' ' + address.compliment}`
        }

        if (!address.city && !address.state) {
            secondAddressLine = address.postalCode || ''
        } else if (!address.city && address.state) {
            secondAddressLine = address.state + `${!address.postalCode ? '' : ' ' + address.postalCode}`
        } else {
            secondAddressLine = address.city + ', ' + address.state + `${!address.postalCode ? '' : ' ' + address.postalCode}`
        }

        return { firstAddressLine, secondAddressLine }
    }

    static formatDefaultValue = (propValue, optionsArray) => {
        if (optionsArray?.length > 0) {
            const findValue = optionsArray.find(option => option.value === propValue)

            if (findValue) {
                return findValue.title
            } else {
                return optionsArray[0].title
            }
        }

        return ''
    }

    /**
     * Method generates variables that are needed in the Working Hours row in the Location Details
     * @returns an object with 2 properties - a boolean which shows if the store is open or closed at the moment
     * and a string that contains a formated text of the next opening/closing time.
     */
    static formatWorkingHoursRow = (locationHours, localizations) => {
        const hoursDict = {
            0: {
                start: locationHours?.sundayStart,
                end: locationHours?.sundayEnd,
            },
            1: {
                start: locationHours?.mondayStart,
                end: locationHours?.mondayEnd,
            },
            2: {
                start: locationHours?.tuesdayStart,
                end: locationHours?.tuesdayEnd,
            },
            3: {
                start: locationHours?.wednesdayStart,
                end: locationHours?.wednesdayEnd,
            },
            4: {
                start: locationHours?.thursdayStart,
                end: locationHours?.thursdayEnd,
            },
            5: {
                start: locationHours?.fridayStart,
                end: locationHours?.fridayEnd,
            },
            6: {
                start: locationHours?.saturdayStart,
                end: locationHours?.saturdayEnd,
            },
        }

        let nextOpenClose = ''
        let day = moment(moment()).day()
        const locationTime = moment.tz(locationHours?.timeZone).format('HH:mm:ss')
        const sameday = hoursDict[day]
        const locationIsOpen = sameday.start < locationTime && sameday.end > locationTime
        const locationWillBeOpen = sameday.start > locationTime

        if (locationIsOpen) {
            nextOpenClose = `${localizations.closesAt} ${moment(sameday.end, 'h:mm a').format('h:mm a')}`
        } else {
            if (locationWillBeOpen) {
                nextOpenClose = `${localizations.opensAt} ${moment(sameday.start, 'h:mm a').format('h:mm a')} `
            } else {
                let foundNextOpen = false
                for (let i = 1; i <= 7 && !foundNextOpen; i++) {
                    const nextDay = (day + i) % 7
                    const nextDayHours = hoursDict[nextDay]
                    if (nextDayHours.start) {
                        nextOpenClose = `${localizations.opens} ${moment.weekdays()[nextDay]} ${localizations.at} ${moment(nextDayHours.start, 'h:mm a').format('h:mm a')} `
                        foundNextOpen = true
                    }
                }
            }
        }

        return { locationIsOpen, nextOpenClose }
    }

    /**
     * Method formats a date range of two dates with initial formats YYYY-MM-DD in multiple formatted string depending on the range
     * @returns a formatted date range string in the following formats:
     * @example Same day: June 3rd, 2024
     * @example Same month: June 03 - 05, 2024
     * @example Different months: Jun 03 - Nov 03, 2024
     * @example Different years: Jun 01, 2024 - Nov 05, 2025
     */
    static formatDateRange = (startDate, endDate) => {
        try {
            const format = 'YYYY-MM-DD'

            const start = moment(startDate, format, true)
            const end = moment(endDate, format, true)

            if (start.isSame(end, 'day')) {
                return start.format('MMMM Do, YYYY')
            }

            if (start.isSame(end, 'year')) {
                return `${start.format(`${start.isSame(end, 'month') ? 'MMMM' : 'MMM'} DD`)} - ${end.format(
                    start.isSame(end, 'month') ? 'DD' : 'MMM DD'
                )}, ${end.format('YYYY')}`
            } else {
                return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`
            }
        } catch {
            return ''
        }
    }

    /**
     * Method formats a 24 hour time string to HH:MM am/pm format
     * @returns a formatted am/pm string of the 24 hour format string input
     */
    static formatTime = time => {
        try {
            const parsedTime = moment(time, 'HH:mm:ss', true)

            if (!parsedTime.isValid()) {
                throw new Error('Invalid time')
            }

            const formattedTime = parsedTime.format('hh:mm A')

            return formattedTime
        } catch {
            return ''
        }
    }

    /**
     * Method formats a url to have a http:// prefix if it doesn't have it
     * @param {string} url
     * @returns a url with http:// prefix if it doesn't have it
     */
    static formatUrl = url => {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url
        }
        return `https://${url}`
    }
}
